import { BaseScreen } from '@goodboydigital/astro';
import { Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { DecoEntity } from '../game/entities/DecoEntity';
import { TrailEntity } from '../game/entities/TrailEntity';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import LocalisedText from '../ui/LocalisedText';
import { StatsWidget } from '../ui/StatsWidget';
import { StoreSelectButton } from './../ui/buttons/StoreSelectButton';

export class RideStudioScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public title!: LocalisedText;
    public backButton!: NineSliceButton;
    public playButton!: NineSliceButton;

    public iconHash: Record<string, Texture>;

    protected bodyStoreButton!: StoreSelectButton;
    protected wheelStoreButton!: StoreSelectButton;
    protected trailStoreButton!: StoreSelectButton;
    protected extrasStoreButton!: StoreSelectButton;

    private _stats!: StatsWidget;

    constructor(app: App)
    {
        super(app);
        this.app = app;
        this.iconHash = {};
    }

    async init(): Promise<void>
    {
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        const backIcon = Sprite.from(Shared.assets.getTexture('icon-back-arrow'));

        this.backButton = new NineSliceButton({
            icon: backIcon,
            accessibleTitle: 'Back',
            w: backIcon.width * 1.5,
            h: backIcon.height * 1.5,
        });
        this.view.addChild(this.backButton.view);
        this.backButton.onPress.connect(() =>
        {
            this.app.controller.toGarage();
        });

        this.title = new LocalisedText('rideStudioScreen_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 62,
            maxWidth: 450,
        });
        this.title.anchor.set(0.5);
        this.view.addChild(this.title);

        this.bodyStoreButton = new StoreSelectButton({
            titleText: 'rideStudioScreen_bodyStore',
            iconSprite: new Sprite(),
        });
        this.bodyStoreButton.onPress.connect(() => {
            this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: "garage|body" });
            this.app.controller.toBodyStore();
        });
        this.view.addChild(this.bodyStoreButton.view);

        this.wheelStoreButton = new StoreSelectButton({
            titleText: 'rideStudioScreen_wheelStore',
            iconSprite: new Sprite(),
        });
        this.wheelStoreButton.onPress.connect(() => {
            this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: "garage|wheels" });
            this.app.controller.toWheelStore();
        });
        this.view.addChild(this.wheelStoreButton.view);

        this.trailStoreButton = new StoreSelectButton({
            titleText: 'rideStudioScreen_trailStore',
            iconSprite: new Sprite(),
            flipped: true,
        });
        this.trailStoreButton.onPress.connect(() => {
            this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: "garage|trails" });
            this.app.controller.toTrailStore();
        });
        this.view.addChild(this.trailStoreButton.view);

        this.extrasStoreButton = new StoreSelectButton({
            titleText: 'rideStudioScreen_extrasStore',
            iconSprite: new Sprite(),
            flipped: true,
        });
        this.extrasStoreButton.onPress.connect(() => {
            this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: "garage|extras" });
            this.app.controller.toExtrasStore()
        });
        this.view.addChild(this.extrasStoreButton.view);

        this._stats = new StatsWidget();
        this.view.addChild(this._stats.view);

        const playIcon = new Sprite();
        const playText = new LocalisedText('garageScreen_play', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 44,
            maxWidth: 250,
        });

        playText.anchor.set(0.5);
        playIcon.addChild(playText);

        this.playButton = new NineSliceButton({
            icon: playIcon,
            accessibleTitle: 'GO',
            bgAsset: 'button-ninesliced-magenta',
            w: 275,
            h: 100,
        });
        this.playButton.onPress.connect(() => {
            this.app.analytics.trackEvent(AnalyticsEvent.GARAGEENTERPAGE_RACE_CLICK);
            this.app.controller.toPreRace();
        });
        this.view.addChild(this.playButton.view);
    }

    show(): void
    {
        this.resize(this.width, this.height);
        
        this.app.analytics.trackState(AnalyticsState.GARAGE_ENTER);

        this.backButton.view.visible = !Settings.spineDebug;

        const garage = Shared.game.garage;

        const body = garage.getPlainSprite({ chassis: garage.selectedChassis, color: garage.selectedColor });
        const wheel = garage.getPlainSprite({ wheelLeft: garage.selectedWheelLeft, color: garage.selectedColorWheelLeft });
        const extra = DecoEntity.mountIcon(garage.selectedExtra);

        body.scale.set(140 / body.width);
        wheel.scale.set(70 / wheel.width);
        extra.scale.set(Math.min(120 / extra.width, 2.2));

        this.bodyStoreButton.updateIcon(body);
        this.wheelStoreButton.updateIcon(wheel);
        this.trailStoreButton.updateIcon(TrailEntity.mountIcon(garage.selectedTrail));
        this.extrasStoreButton.updateIcon(extra);

        this.view.visible = true;

        this.playButton.view.visible = false;
        this.playButton.show(true);
        Shared.game.soundSystem.playMusic('menu-music');
        !Settings.spineDebug && this._stats.open();
    }

    public hide(): void
    {
        this.playButton.hide(true);
    }

    hidden(): void
    {
        this.view.visible = false;

        this._stats.close();
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.backButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );

        this.title.x = this.app.stageCenter.x;
        this.title.y = this.app.stageTop + (this.title.height / 2) + 20;

        this.bodyStoreButton.view.position.set(
            this.app.stageLeft + 130,
            this.app.stageCenter.y - 90,
        );
        this.bodyStoreButton.originalX = this.app.stageLeft + 130;

        this.wheelStoreButton.view.position.set(
            this.app.stageLeft + 130,
            this.app.stageCenter.y + 90,
        );
        this.wheelStoreButton.originalX = this.app.stageLeft + 130;

        this.trailStoreButton.view.position.set(
            this.app.stageRight - 130,
            this.app.stageCenter.y - 90,
        );
        this.trailStoreButton.originalX = this.app.stageRight - 130;

        this.extrasStoreButton.view.position.set(
            this.app.stageRight - 130,
            this.app.stageCenter.y + 90,
        );
        this.extrasStoreButton.originalX = this.app.stageRight - 130;

        this._stats.view.x = this.app.stageCenter.x + 55;
        this._stats.view.y = this.app.stageBottom - 300;

        this.playButton.view.position.set(
            this.app.stageRight - 170,
            this.app.stageBottom - 90);
    }
}
